import { z, ZodError, type ZodIssue } from "zod";
import type { ValidationErrors } from "../types";

/**
 * Recursively collect all leaf Zod issues,
 * accumulating their full paths.
 */
function collectLeafIssues(
  issue: ZodIssue,
  parentPath: PropertyKey[] = []
): Array<{ path: PropertyKey[]; message: string }> {
  const currentPath = [...parentPath, ...issue.path];

  // Zod nests errors via `errors` for unions, intersections, etc.
  if ("errors" in issue && Array.isArray(issue.errors)) {
    return issue.errors.flatMap(branch =>
      Array.isArray(branch)
        ? branch.flatMap(nested => collectLeafIssues(nested, currentPath))
        : collectLeafIssues(branch, currentPath)
    );
  }

  // Leaf issue → return with full path
  return [
    {
      path: currentPath,
      message: issue.message ?? issue.code,
    },
  ];
}

/**
 * Set a nested error message following a full path.
 * Supports arbitrary depth (objects, arrays, etc).
 */
function setErrorPath(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: Record<string, any>,
  path: PropertyKey[],
  message: string
) {
  let current = target;

  for (let i = 0; i < path.length; i++) {
    const key = String(path[i]);

    // Last segment → assign message
    if (i === path.length - 1) {
      if (current[key] == null) {
        current[key] = message;
      }
      return;
    }

    if (current[key] == null || typeof current[key] !== "object") {
      current[key] = {};
    }

    current = current[key];
  }
}

export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; errors: ValidationErrors<T> | undefined };

/**
 * Validate a value against a Zod schema and map errors
 * into a nested object compatible with forms.
 */
export const validateSchema = <T>(
  schema: z.ZodType<T>,
  value: unknown
): ValidationResult<T> => {
  const result = schema.safeParse(value);
  if (result.success) return { success: true, data: result.data };

  const zodError: ZodError = result.error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors: Record<string, any> = {};

  const leafIssues = zodError.issues.flatMap(issue => collectLeafIssues(issue));

  for (const { path, message } of leafIssues) {
    if (!path.length) continue;
    setErrorPath(errors, path, message);
  }

  return { success: false, errors: errors as ValidationErrors<T> };
};
